import { z } from 'zod';
import { GlossaryTermIdSchema } from './glossary.js';

export const UserLearningProgressSchema = z.object({
  userId: z.string(),
  lessonId: z.string(),
  completedStepIds: z.array(z.string()).default([]),
  startedAt: z.string(),
  lastActivityAt: z.string(),
  completedAt: z.string().optional(),
  bookmarkedTermIds: z.array(GlossaryTermIdSchema).default([]),
  quizScores: z.record(z.number().min(0).max(1)).default({}),
});

export type UserLearningProgress = z.infer<typeof UserLearningProgressSchema>;

/** Outcome horizons (Section 16.4). Tactical is isolated from the core book. */
export const LearningOutcomeHorizonSchema = z.enum([
  '1d',
  '5d',
  '30d',
  '90d',
  '6m',
  '12m',
  '24m',
]);

export type LearningOutcomeHorizon = z.infer<typeof LearningOutcomeHorizonSchema>;

export const OutcomeObservationSchema = z.object({
  outcomeId: z.string(),
  decisionId: z.string(),
  securityId: z.string().optional(),
  horizon: LearningOutcomeHorizonSchema,
  /** Date-driven maturity — an outcome is immature until its horizon date. */
  maturesAt: z.string(),
  observedAt: z.string().optional(),
  isMature: z.boolean(),
  returnPct: z.number().nullable(),
  benchmarkReturnPct: z.number().nullable(),
  maxDrawdownPct: z.number().nullable(),
  /** Proxy outcomes never enter live P&L (Section 16.3). */
  isProxy: z.boolean(),
  sourceIds: z.array(z.string()).min(1),
});

export type OutcomeObservation = z.infer<typeof OutcomeObservationSchema>;

/** A rejected candidate followed counterfactually (Section 16.2). */
export const CounterfactualOutcomeSchema = z.object({
  counterfactualId: z.string(),
  decisionId: z.string(),
  securityId: z.string(),
  rejectedAtStage: z.string(),
  rejectionReason: z.string(),
  horizon: LearningOutcomeHorizonSchema,
  maturesAt: z.string(),
  isMature: z.boolean(),
  hypotheticalReturnPct: z.number().nullable(),
  benchmarkReturnPct: z.number().nullable(),
  sourceIds: z.array(z.string()).min(1),
});

export type CounterfactualOutcome = z.infer<typeof CounterfactualOutcomeSchema>;

/**
 * Decision Value decomposition (Section 16.3). Components are reported
 * separately — never collapsed into a single scalar.
 */
export const DecisionValueComponentsSchema = z.object({
  avoidedLossValue: z.number().nullable(),
  missedUpsideCost: z.number().nullable(),
  sizingValue: z.number().nullable(),
  holdingValue: z.number().nullable(),
  cashOptionalityValue: z.number().nullable(),
  afterTaxReturn: z.number().nullable(),
  benchmarkRelativeReturn: z.number().nullable(),
  drawdownVolatilityCost: z.number().nullable(),
});

export type DecisionValueComponents = z.infer<typeof DecisionValueComponentsSchema>;

export const CalibrationBucketSchema = z.object({
  bucket: z.string(),
  meanForecastProbability: z.number().min(0).max(1),
  realizedFrequency: z.number().min(0).max(1),
  sampleCount: z.number().int().nonnegative(),
  /** Clustered effective sample (Section 16.6) — never inflated by repeats. */
  effectiveSampleCount: z.number().int().nonnegative(),
});

export type CalibrationBucket = z.infer<typeof CalibrationBucketSchema>;

export const LearningOverviewSchema = z.object({
  asOf: z.string(),
  policyVersion: z.string(),
  matureDecisionCount: z.number().int().nonnegative(),
  immatureDecisionCount: z.number().int().nonnegative(),
  brierScore: z.number().nullable(),
  calibration: z.array(CalibrationBucketSchema).default([]),
  decisionValue: DecisionValueComponentsSchema,
  valuationIntervalHitRate: z.number().nullable(),
  liveProxySeparationNote: z.string(),
  sampleCaveats: z.array(z.string()).default([]),
});

export type LearningOverview = z.infer<typeof LearningOverviewSchema>;
